import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AllocateOperatorAccessPayload, OperatorAccess, PermissionsList, UpdatePermissionsPayload, UserFunction, UserMultipleFunctii } from './operatori.interface';

@Injectable({
    providedIn: 'root',
})
export class OperatoriService {
    private token!: string;
    private httpOptions: any;

    private userAccessSubject = new BehaviorSubject<OperatorAccess[]>([]);
    public userAccess$ = this.userAccessSubject.asObservable();

    private userFunctionsSubject = new BehaviorSubject<any[]>([]);
    public userFunctions$ = this.userFunctionsSubject.asObservable();

    private basePath = environment.interop.basePath;

    private getPermisiuniListUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.operatori
            .getOperatorPermisiuniList;

    private updateOperatorUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.operatori.updateOperatorPermisiuni;

    private getOperatorFunctieUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.getOperatorFunctie;
    private updateOperatorFunctieUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.updateOperatorFunctie;
    private getOperatorAccessUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.getOperatorAccess;
    private operatorAccessListUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.operatorAccessList;
    public operatorAccessListDtUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.operatorAccessListDt;
    private allocateOperatorAccessUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.allocateOperatorAccess;
    private deleteOperatorAccessUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.deleteOperatorAccess;
    private activeOperatorAccessUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.activeOperatorAccess;

    // New multi-function API endpoints
    private getUserMultipleFunctiiUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.getUserMultipleFunctii;
    private updateUserMultipleFunctiiUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.updateUserMultipleFunctii;
    private setActiveFunctieUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.setActiveFunctie;
    private getActiveFunctieUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.getActiveFunctie;
    private getUserFunctiiListUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.getUserFunctiiList;
    private userHasFunctionsUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.userHasFunctions;

    // Enhanced features API endpoints
    private functieHistoryUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.functieHistory;
    private functieHistoryExportUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.functieHistoryExport;
    private functieHistoryStatisticsUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.functieHistoryStatistics;
    private recentFunctionChangesUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.recentFunctionChanges;
    private bulkAssignmentUsersUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.bulkAssignmentUsers;
    private bulkAssignmentPreviewUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.bulkAssignmentPreview;
    private bulkAssignmentExecuteUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.bulkAssignmentExecute;
    private bulkAssignmentHistoryUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.bulkAssignmentHistory;
    private bulkAssignmentTemplatesUrl = this.basePath + environment.interop.indoc.nomenclatoare.registratura.operatori.bulkAssignmentTemplates;

    constructor(private http: HttpClient) { }

    permisiuniList(id: number) {
        return this.http.get<{ errors: boolean; data: PermissionsList; status_code: number }>(
            this.getPermisiuniListUrl + id,
        );
    }

    updateOperator(id: number, data: UpdatePermissionsPayload) {
        return this.http.post(this.updateOperatorUrl + id, data);
    }

    getOperatorFunctie(id: number) {
        return this.http.get(this.getOperatorFunctieUrl + id);
    }

    updateOperatorFunctie(user_id: number, data: { functie_id: number }) {
        return this.http.put(this.updateOperatorFunctieUrl + user_id, data);
    }

    getOperatorAccess() {
        return this.http.get<{ errors: boolean; data: OperatorAccess[], status_code: number }>(this.getOperatorAccessUrl).pipe(
            tap((res) => {
                const userAccess = res.data || [];
                this.userAccessSubject.next(userAccess);
            })
        );
    }

    operatorAccessList() {
        return this.http.get(this.operatorAccessListUrl);
    }

    allocateOperatorAccess(data: AllocateOperatorAccessPayload) {
        return this.http.post(this.allocateOperatorAccessUrl, data);
    }

    deleteOperatorAccess(id_access: number) {
        return this.http.delete(this.deleteOperatorAccessUrl + id_access);
    }

    activeOperatorAccess(id_access: number) {
        return this.http.put(this.activeOperatorAccessUrl + id_access, {});
    }

    // New multi-function methods

    /**
     * Get all functions assigned to a specific user with details
     * @param userId - The ID of the user
     * @returns Observable with user functions data
     */
    getUserMultipleFunctii(userId: number) {
        return this.http.get<{ data: UserMultipleFunctii, errors: boolean, status_code: number }>(
            this.getUserMultipleFunctiiUrl + userId
        ).pipe(
            tap((res) => {
                const userFunctions = res.data?.functions || [];
                this.userFunctionsSubject.next(userFunctions);
            })
        );
    }

    get currentUserFunctions() {
        return this.userFunctionsSubject.value;
    }

    /**
     * Get all users with a specific function
     * @param functieId - The ID of the function
     * @param onlyActive - Whether to only return users with active functions
     * @returns Observable with users data
     */
    getUsersByFunctie(functieId: number, onlyActive: boolean = false) {
        // Use the users-by-functie endpoint directly (basePath already includes /api)
        // Add query parameter for only_active if needed
        let url = `${this.basePath}api/user/operators-by-functie/${functieId}`;
        if (onlyActive) {
            url += '?only_active=true';
        }
        return this.http.get<any>(url, this.httpOptions).pipe(
            map((response: any) => {
                // Check if response has the expected structure
                // Backend returns: { errors: false, data: { data: [...], count: X, functie_id: Y } }
                if (response && response.data) {
                    // Extract the actual user array from nested structure
                    const userData = response.data.data || response.data;

                    // Only process if userData is an array
                    if (Array.isArray(userData)) {
                        const processedData = userData.map((user: any) => ({
                            ...user,
                            // These fields might already be set by backend, but ensure they exist
                            is_active_function: user.is_active_function !== undefined
                                ? user.is_active_function
                                : (user.user_functions?.find((f: any) =>
                                    f.functie_id === functieId && f.is_active
                                )?.is_active || false),
                            user_functie_id: user.user_functie_id ||
                                user.user_functions?.find((f: any) =>
                                    f.functie_id === functieId
                                )?.id
                        }));

                        // Return in the expected format
                        return {
                            data: processedData,
                            errors: false,
                            count: response.data.count || processedData.length,
                            functie_id: response.data.functie_id || functieId,
                            source: response.data.source
                        };
                    } else {
                        // If userData is not an array, return it as is (might be an object with users)
                        return {
                            data: userData,
                            errors: false
                        };
                    }
                }

                // Return empty if no valid response
                return { data: [], errors: false };
            }),
            catchError((error) => {
                console.error('Error fetching users by function:', error);
                // Return empty data on error
                return throwError(() => ({ data: [], errors: true }));
            })
        );
    }

    /**
     * Update multiple functions for a user
     * @param userId - The ID of the user
     * @param payload - Object containing functie_ids array and active_functie_id
     * @returns Observable with update result
     */
    updateUserMultipleFunctii(userId: number, payload: { functie_ids: number[]; active_functie_id: number }) {
        return this.http.post(this.updateUserMultipleFunctiiUrl + userId, payload);
    }

    /**
     * Set active function for a user
     * @param userId - The ID of the user
     * @param functieId - The ID of the function to set as active
     * @returns Observable with result
     */
    setActiveFunctie(userId: number, functieId: number) {
        return this.http.post(this.setActiveFunctieUrl + userId, { functie_id: functieId });
    }

    /**
     * Get the currently active function for a user
     * @param userId - The ID of the user
     * @returns Observable with active function data
     */
    getActiveFunctie(userId: number) {
        return this.http.get(this.getActiveFunctieUrl + userId);
    }

    /**
     * Get basic function info for all assigned functions (for dropdown/selector)
     * @param userId - The ID of the user
     * @returns Observable with functions list
     */
    getUserFunctiiList(userId: number) {
        return this.http.get(this.getUserFunctiiListUrl + userId);
    }

    /**
     * Check if user has any functions assigned
     * @param userId - The ID of the user
     * @returns Observable with boolean result
     */
    userHasFunctions(userId: number) {
        return this.http.get(this.userHasFunctionsUrl + userId);
    }

    // Enhanced Features Methods

    /**
     * Get function history for a specific user
     * @param userId - The ID of the user
     * @param limit - Maximum number of history records to retrieve
     * @returns Observable with history data
     */
    getUserFunctionHistory(userId: number, limit: number = 50) {
        return this.http.get(this.functieHistoryUrl + userId + `?limit=${limit}`);
    }

    /**
     * Export function history for a user
     * @param userId - The ID of the user
     * @param format - Export format (json, csv, excel, pdf)
     * @param startDate - Optional start date filter
     * @param endDate - Optional end date filter
     * @returns Observable with export data
     */
    exportUserFunctionHistory(userId: number, format: string = 'json', startDate?: string, endDate?: string) {
        const payload: any = { format };
        if (startDate) payload.start_date = startDate;
        if (endDate) payload.end_date = endDate;

        return this.http.post(this.functieHistoryExportUrl + userId, payload);
    }

    /**
     * Get function change statistics
     * @param startDate - Optional start date filter
     * @param endDate - Optional end date filter
     * @returns Observable with statistics data
     */
    getFunctionHistoryStatistics(startDate?: string, endDate?: string) {
        let url = this.functieHistoryStatisticsUrl;
        const params = [];
        if (startDate) params.push(`start_date=${startDate}`);
        if (endDate) params.push(`end_date=${endDate}`);
        if (params.length > 0) url += '?' + params.join('&');

        return this.http.get(url);
    }

    /**
     * Get recent function changes across all users
     * @param limit - Maximum number of recent changes
     * @param days - Number of days to look back
     * @returns Observable with recent changes data
     */
    getRecentFunctionChanges(limit: number = 20, days: number = 7) {
        return this.http.get(this.recentFunctionChangesUrl + `?limit=${limit}&days=${days}`);
    }

    /**
     * Get users available for bulk assignment
     * @param filters - Filter criteria object
     * @returns Observable with users data
     */
    getBulkAssignmentUsers(filters: any = {}) {
        return this.http.get(this.bulkAssignmentUsersUrl, { params: filters });
    }

    /**
     * Preview bulk assignment changes
     * @param payload - Bulk assignment payload
     * @returns Observable with preview data
     */
    previewBulkAssignment(payload: any) {
        return this.http.post(this.bulkAssignmentPreviewUrl, payload);
    }

    /**
     * Execute bulk assignment
     * @param payload - Bulk assignment payload with reason
     * @returns Observable with execution results
     */
    executeBulkAssignment(payload: any) {
        return this.http.post(this.bulkAssignmentExecuteUrl, payload);
    }

    /**
     * Get bulk assignment history
     * @param limit - Maximum number of history records
     * @returns Observable with bulk assignment history
     */
    getBulkAssignmentHistory(limit: number = 20) {
        return this.http.get(this.bulkAssignmentHistoryUrl + `?limit=${limit}`);
    }

    /**
     * Get bulk assignment templates
     * @returns Observable with predefined templates
     */
    getBulkAssignmentTemplates() {
        return this.http.get(this.bulkAssignmentTemplatesUrl);
    }

    setToken(token: string) {
        this.token = token;
        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${this.token}`,
            }),
        };
    }
}
